import { appendAdjustedCostPriceColumn } from "../../../utils/reports/reportHoldingColumns";
import {
  appendHoldingLevelCostColumnsWithDpName,
  orderTransactionCostBenchmarkAndMemberColumns,
  withTxnMetadataAndBenchmarkColumns,
} from "../../../utils/reports/transactionMetadataColumns";
import {
  FieldColumn,
  INVESTOR_COLUMN,
  OTHER_ASSET_HOLDING_COLUMNS,
  OTHER_ASSET_TABLE_SEGMENT,
  OTHER_ASSET_TRANSACTION_COLUMNS,
} from "./otherAssetReportConfig";

export const REPORT_BUILDER_VISIBLE_COLUMN_COUNT = 7;

const OTHER_ASSET_HIDDEN_COLUMN_KEYS = new Set(["isin"]);

const UNREALIZED_CAGR_COLUMN: FieldColumn = { key: "cagr", value: "CAGR", minWidth: 96 };

const PNL_COLUMN_KEYS = new Set([
  "gainLoss",
  "gainLossPercentage",
  "gainLossPerc",
  "unrealisedPNL",
  "unrealisedPNLPercentage",
  "realisedPnl",
  "realisedPNLPercentage",
  "unrealisedGainLoss",
  "unrealisedGainLossPercentage",
  "realisedGainLoss",
]);

const REPORT_DAYS_COLUMN: FieldColumn = { key: "days", value: "Avg. Days", minWidth: 88 };

const REPORT_COST_COLUMN_KEYS = ["stt", "stampDuty", "otherExpenses", "brokerage"] as const;

const stripHiddenColumns = (cols: FieldColumn[]): FieldColumn[] =>
  cols.filter((c) => !OTHER_ASSET_HIDDEN_COLUMN_KEYS.has(c.key));

const withTxnSchemeNameColumn = (cols: FieldColumn[]): FieldColumn[] => {
  if (cols.some((c) => c.key === "schemeName")) return cols;
  return [{ key: "schemeName", value: "Name", locked: true, minWidth: 200 }, ...cols];
};

const withUnrealizedCagrColumn = (cols: FieldColumn[]): FieldColumn[] => {
  if (cols.some((c) => c.key === "cagr")) return cols;
  const without = cols.filter((c) => c.key !== "cagr");
  const pnlIdx = without.findIndex((c) => PNL_COLUMN_KEYS.has(c.key));
  const insertAt = pnlIdx >= 0 ? pnlIdx : without.length;
  return [...without.slice(0, insertAt), UNREALIZED_CAGR_COLUMN, ...without.slice(insertAt)];
};

const stripMemberNameColumn = (cols: FieldColumn[]): FieldColumn[] =>
  orderTransactionCostBenchmarkAndMemberColumns(cols, {
    includeMemberName: false,
  }) as FieldColumn[];

const stripDaysColumn = (columns: FieldColumn[]): FieldColumn[] =>
  columns.filter((c) => c.key !== "days");

const ensureReportDaysColumn = (columns: FieldColumn[]): FieldColumn[] => {
  if (columns.some((c) => c.key === "days")) return columns;
  return [...columns, REPORT_DAYS_COLUMN];
};

const orderDaysColumnBeforeStt = (columns: FieldColumn[]): FieldColumn[] => {
  const withoutDays = columns.filter((c) => c.key !== "days");
  const daysCol = columns.find((c) => c.key === "days") ?? REPORT_DAYS_COLUMN;
  const firstCostIdx = withoutDays.findIndex((c) =>
    REPORT_COST_COLUMN_KEYS.includes(c.key as (typeof REPORT_COST_COLUMN_KEYS)[number]),
  );
  const insertAt = firstCostIdx >= 0 ? firstCostIdx : withoutDays.length;
  return [...withoutDays.slice(0, insertAt), daysCol, ...withoutDays.slice(insertAt)];
};

const appendOtherAssetCostColumns = (
  columns: FieldColumn[],
  options?: { placeDaysBeforeStt?: boolean },
): FieldColumn[] => {
  let cols = columns;
  if (options?.placeDaysBeforeStt) {
    cols = stripDaysColumn(cols);
  }
  cols = stripMemberNameColumn(cols);
  if (options?.placeDaysBeforeStt) {
    cols = orderDaysColumnBeforeStt(ensureReportDaysColumn(cols));
  }
  return cols;
};

const buildOtherAssetHoldingColumns = (): FieldColumn[] => {
  let cols = appendAdjustedCostPriceColumn(
    withUnrealizedCagrColumn(OTHER_ASSET_HOLDING_COLUMNS),
  );
  cols = appendHoldingLevelCostColumnsWithDpName(cols, OTHER_ASSET_TABLE_SEGMENT);
  return stripHiddenColumns(
    appendOtherAssetCostColumns(cols, { placeDaysBeforeStt: true }),
  );
};

const buildOtherAssetTransactionColumns = (): FieldColumn[] => {
  let cols = withTxnSchemeNameColumn(
    withTxnMetadataAndBenchmarkColumns(
      OTHER_ASSET_TRANSACTION_COLUMNS,
      OTHER_ASSET_TABLE_SEGMENT,
      { includeBenchmarks: false },
    ) as FieldColumn[],
  );
  cols = withUnrealizedCagrColumn(cols);
  return stripHiddenColumns(stripMemberNameColumn(cols));
};

export const getBaseColumnsForReportType = (reportType: string): FieldColumn[] => {
  switch (reportType) {
    case "Transaction":
      return buildOtherAssetTransactionColumns();
    case "Holding":
    default:
      return buildOtherAssetHoldingColumns();
  }
};

export const buildSelectableColumns = (reportType: string): FieldColumn[] => [
  INVESTOR_COLUMN,
  ...getBaseColumnsForReportType(reportType),
];

export const buildEffectiveColumns = (
  reportType: string,
  visibleColumnKeys: string[],
): FieldColumn[] => {
  let columns = buildSelectableColumns(reportType);
  if (visibleColumnKeys.length > 0) {
    const visible = new Set(visibleColumnKeys);
    columns = columns.filter((c) => visible.has(c.key));
  }
  return columns;
};

export const getDefaultVisibleColumnKeys = (reportType: string): string[] =>
  buildSelectableColumns(reportType).map((c) => c.key);
